import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDateTime } from '../../../lib/format';
import type { AuditRow, NumberedPage, PlatformUserRow } from '@dru/types';
import {
  DataTable, Drawer, EmptyState, EntityCombobox, FilterBar, Facts, Input, Pagination, SearchInput, StatusBadge, cn,
} from '@dru/ui';
import {
  Activity as ActivityIcon, Eye, LogIn, Pencil, Plus, ShieldAlert, Trash2, UserCog, type LucideIcon,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';

/**
 * The trail.
 *
 * Append-only at the database — the application role has no UPDATE or DELETE
 * grant on this table and a trigger enforces the same — so this screen is a
 * reader and there is deliberately nothing on it that changes what a row says.
 *
 * It is read to answer four questions fast: who, did what, to which record,
 * when. So each row says those in words — "Test changed", "Signed in",
 * "Role permissions changed" — with the exact action key one click away in
 * the detail panel, where somebody quoting it to an auditor needs it.
 */

/* ── Words for what the trail stores as keys ─────────────────── */

/** The whole action, where the generic rule below would read badly. */
const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.login-failed': 'Sign-in refused',
  'auth.mfa-challenged': 'Asked for second factor',
  'auth.mfa-disabled': 'Two-factor turned off',
  'auth.mfa-enabled': 'Two-factor turned on',
  'auth.mfa-enrolment-started': 'Two-factor set-up started',
  'auth.mfa-failed': 'Second factor refused',
  'auth.mfa-recovery-codes-reissued': 'Recovery codes reissued',
  'auth.mfa-recovery-code-used': 'Recovery code used',
  'auth.otp-failed': 'One-time code refused',
  'auth.otp-requested': 'One-time code requested',
  'auth.otp-verified': 'One-time code accepted',
  'auth.password-reset-requested': 'Password reset requested',
  'platform.billing': 'Read company billing',
  'platform.operate': 'Read across companies',
  'platform.overview.read': 'Read the platform overview',
  'platform.dashboard.pipeline': 'Read the case pipeline',
  'platform.dashboard.alerts': 'Read platform alerts',
  'platform.ledger.sweep': 'Swept a ledger across companies',
  'platform.setup': 'Company setup support',
  'platform.staffpay': 'Read staff payout',
  'platform.provision': 'Provisioned a company',
  'platform.health-report-recovery': 'Recovered a health report',
  'platform.impersonation.start': 'Started impersonating',
  'platform.impersonation.end': 'Stopped impersonating',
  'platform.settings.smtp': 'Changed email settings',
  'platform.settings.smtp-test': 'Sent a test email',
  'platform.settings.support': 'Changed support contact',
  'platform.settings.template': 'Changed message wording',
  'catalogue.protocol.version': 'Published a protocol version',
  'catalogue.test.ranges': 'Changed reference bands',
  'catalogue.test.answer-options': 'Changed allowed answers',
  'catalogue.industry.hazards-set': 'Changed typical hazards',
  'catalogue.industry.packages-set': 'Changed suggested packages',
  'catalogue.rule.retire': 'Retired a recommendation rule',
  'role.created': 'Role created',
  'role.deleted': 'Role deleted',
  'role.updated': 'Role renamed',
  'role.permissions-changed': 'Role permissions changed',
  'user.role-change': 'Role changed',
  'user.sessions-revoked': 'Sessions ended',
  'user.invitation-sent': 'Invitation sent',
  'user.invitation-redeemed': 'Invitation accepted',
  'user.invitation-refused': 'Invitation link refused',
  'tenant.register': 'Company registered',
  'tenant.register.email-failed': 'Registration email failed',
  'tenant.welcome-resend': 'Welcome email resent',
  'physician.certify': 'Certified fitness',
  'lab.result.release': 'Released a lab report',
  'lab.critical.raised': 'Critical value raised',
  'invoice.payment-recorded': 'Payment recorded',
  'invoice.payment-reversed': 'Payment reversed',
  'certificate.generated': 'Certificate generated',
};

/** The noun a key's first segments name. */
const NOUN: Record<string, string> = {
  tenant: 'Company', user: 'User', role: 'Role', lead: 'Lead', partner: 'Lab partner',
  physician: 'Physician', invoice: 'Invoice', certificate: 'Certificate', employee: 'Employee',
  examination: 'Medical case', encounter: 'Visit', appointment: 'Appointment', campaign: 'Campaign',
  test: 'Test', hazard: 'Hazard', protocol: 'Protocol', package: 'Package', industry: 'Industry',
  restriction: 'Restriction code', rule: 'Recommendation rule', plant: 'Plant', jobrole: 'Job role',
  department: 'Department', designation: 'Designation', contractor: 'Contractor', quote: 'Quote',
  ratecard: 'Rate card', settlement: 'Settlement', staffpayment: 'Staff payment', consent: 'Consent',
  onboarding: 'Onboarding', settings: 'Settings', report: 'Report', health: 'Health', lab: 'Lab',
  location: 'Branch', sample: 'Sample', result: 'Result',
};

const VERB: Record<string, string> = {
  create: 'created', update: 'changed', delete: 'deleted', retire: 'retired', approve: 'approved',
  reject: 'rejected', suspend: 'suspended', reinstate: 'reinstated', revoke: 'revoked', issue: 'issued',
  publish: 'published', cancel: 'cancelled', convert: 'converted', reopen: 'reopened', download: 'downloaded',
  view: 'viewed', generate: 'generated', generated: 'generated', verify: 'verified', amend: 'amended',
  enter: 'entered', release: 'released', validate: 'validated', collect: 'collected', checkin: 'checked in',
  complete: 'completed', launch: 'launched', activate: 'activated', reschedule: 'rescheduled',
  assemble: 'assembled', finalise: 'finalised', transfer: 'transferred', deactivate: 'deactivated',
  capture: 'captured', upload: 'uploaded', read: 'read', close: 'closed', void: 'voided', accept: 'accepted',
  send: 'sent', reassign: 'reassigned', acknowledge: 'acknowledged', raised: 'raised',
};

function words(key: string): string {
  const spaced = key.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** "catalogue.test.update" → "Test changed". Never the raw key. */
export function actionLabel(action: string): string {
  if (ACTION_LABEL[action]) return ACTION_LABEL[action]!;
  const parts = action.split('.').filter((p) => p !== 'catalogue');
  const verb = parts.pop() ?? action;
  const noun = parts.map((p) => NOUN[p] ?? words(p)).join(' ').toLowerCase();
  const said = VERB[verb] ?? words(verb).toLowerCase();
  const sentence = noun ? `${noun} ${said}` : said;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

const ENTITY_LABEL: Record<string, string> = {
  Tenant: 'Company', UserAccount: 'User account', Partner: 'Lab partner', PartnerLocation: 'Branch',
  ExaminationOrder: 'Medical case', Encounter: 'Visit', TestResult: 'Test result',
  PhysicianReview: 'Physician review', FitnessCertificate: 'Certificate', TestDefinition: 'Test',
  HazardCategory: 'Hazard', HealthPackage: 'Package', RestrictionCode: 'Restriction code',
  RecommendationRule: 'Recommendation rule', Platform: 'Platform', PlatformSetting: 'Platform setting',
  ImpersonationSession: 'Impersonation', StaffInvitation: 'Invitation', MedicalConfiguration: 'Medical configuration',
  PayoutRateCard: 'Payout rate card', PayeeRateCard: 'Payee rate card', InternalCostRule: 'Cost rule',
};

export function entityLabel(type: string): string {
  return ENTITY_LABEL[type] ?? words(type);
}

const ROLE_LABEL: Record<string, string> = {
  PLATFORM_ADMIN: 'Platform admin', PLATFORM_OPS: 'Operations', SUPPORT: 'Support',
  PLATFORM_FINANCE: 'Finance', HR_ADMIN: 'Company admin', EHS_OFFICER: 'EHS officer',
  HR_EXECUTIVE: 'HR executive', PLANT_HR: 'Plant HR', PLANT_ACCOUNTS: 'Plant accounts',
  PARTNER_ADMIN: 'Venue admin', HOSPITAL_COORDINATOR: 'Check-in coordinator',
  HOSPITAL_TECHNICIAN: 'Hospital technician', LAB_TECHNICIAN: 'Lab technician',
  PATHOLOGIST: 'Pathologist', PHYSICIAN: 'Physician', EMPLOYEE: 'Employee',
};

export function roleLabel(key: string | null | undefined): string {
  if (!key) return 'System';
  return ROLE_LABEL[key] ?? words(key);
}

function meta(row: AuditRow): Record<string, unknown> {
  return row.metadata && typeof row.metadata === 'object' ? row.metadata as Record<string, unknown> : {};
}

/** The name of the record, when the row carries one; otherwise its short id. */
function recordName(row: AuditRow): string | null {
  const m = meta(row);
  for (const k of ['name', 'code', 'email', 'what']) {
    if (typeof m[k] === 'string' && m[k]) return m[k] as string;
  }
  const after = row.after && typeof row.after === 'object' ? row.after as Record<string, unknown> : {};
  if (typeof after.name === 'string') return after.name;
  if (!row.entityId) return null;
  // An id that is not a uuid ("physician panel workload") is already words.
  return /^[0-9a-f-]{36}$/.test(row.entityId) ? `#${row.entityId.slice(0, 8)}` : row.entityId;
}

function Outcome({ outcome }: { outcome: string }) {
  if (outcome === 'SUCCESS') return <span className="text-caption text-neutral-500">Done</span>;
  return <StatusBadge tone="danger">{outcome === 'DENIED' ? 'Refused' : words(outcome)}</StatusBadge>;
}

/* ── The page ─────────────────────────────────────────────────── */

/**
 * What a platform reader can narrow to. A prefix of the action key, because
 * the API filters by prefix; the reader picks an area rather than learning
 * the key list.
 */
const AREAS = [
  { value: 'auth.', label: 'Sign-ins and two-factor' },
  { value: 'user.', label: 'Users' },
  { value: 'role.', label: 'Roles and permissions' },
  { value: 'tenant.', label: 'Companies' },
  { value: 'partner.', label: 'Lab network' },
  { value: 'physician.', label: 'Physicians' },
  { value: 'lead.', label: 'Leads' },
  { value: 'catalogue.', label: 'Clinical masters' },
  { value: 'invoice.', label: 'Invoices and payments' },
  { value: 'platform.impersonation', label: 'Impersonation' },
  { value: 'platform.settings', label: 'Platform settings' },
  { value: 'platform.', label: 'Reads across companies' },
];

/** The record types that carry platform rows, in words. */
const RECORD_TYPES = [
  'UserAccount', 'Role', 'Tenant', 'Partner', 'Physician', 'Lead', 'Invoice',
  'TestDefinition', 'HazardCategory', 'Protocol', 'HealthPackage', 'Industry',
  'RecommendationRule', 'RestrictionCode', 'ImpersonationSession', 'Platform', 'PlatformSetting',
].map((t) => ({ value: t, label: entityLabel(t) }));

/** Who did it, as the role filter offers it. `SYSTEM` is a row no person made. */
const ROLE_OPTIONS = [
  ...Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label })),
  { value: 'SYSTEM', label: 'System (automatic)' },
];

/**
 * Twenty-five entries a page: the timeline folds repeats, so a page reads as
 * fewer lines than it fetched.
 */
const PAGE_SIZE = 25;

/**
 * Action keys whose wording on this screen contains the search, so typing
 * "signed in" finds `auth.login` although the trail stores only the key.
 */
function actionsWorded(term: string): string[] {
  const t = term.trim().toLowerCase();
  if (t.length < 2) return [];
  return Object.entries(ACTION_LABEL)
    .filter(([, label]) => label.toLowerCase().includes(t))
    .map(([key]) => key);
}

/**
 * Names for the people on the rows in view, so "who" is a name rather than a
 * uuid prefix. The audit response carries only `actorId`, so each unknown id
 * is looked up once (`/admin/platform-users/:id`) and remembered, and a
 * sign-in — which names the account by email before there is an actor — is
 * matched by searching that email. Only what is on screen is fetched: the
 * old list of the first hundred operators missed everyone after them.
 */
function useActorNames(rows: AuditRow[]) {
  const { permissions } = useAuth();
  const canRead = permissions.has('user.read');
  const cache = useRef(new Map<string, string | null>());
  const [, bump] = useState(0);

  useEffect(() => {
    if (!canRead) return;
    const ids = new Set<string>();
    const emails = new Set<string>();
    for (const r of rows) {
      if (r.actorId && !cache.current.has(r.actorId)) ids.add(r.actorId);
      const m = meta(r);
      if (!r.actorId && r.action.startsWith('auth.') && typeof m.email === 'string') {
        const e = m.email.toLowerCase();
        if (!cache.current.has(e)) emails.add(e);
      }
    }
    if (!ids.size && !emails.size) return;
    // Marked as asked before the answers land, so a re-render asks nothing twice.
    for (const k of [...ids, ...emails]) cache.current.set(k, null);
    let live = true;
    void Promise.all([
      ...[...ids].map((id) => api.get<{ id: string; fullName: string }>(`/admin/platform-users/${id}`)
        .then((u) => { cache.current.set(id, u.fullName); }).catch(() => undefined)),
      ...[...emails].map((e) => api.get<{ items: PlatformUserRow[] }>(`/admin/platform-users?search=${encodeURIComponent(e)}&limit=5`)
        .then((page) => {
          const u = page.items.find((x) => x.email.toLowerCase() === e);
          if (u) { cache.current.set(e, u.fullName); cache.current.set(u.id, u.fullName); }
        }).catch(() => undefined)),
    ]).then(() => { if (live) bump((n) => n + 1); });
    return () => { live = false; };
  }, [rows, canRead]);

  // A fresh Map each render keeps the consumers' memo dependencies honest.
  const out = new Map<string, string>();
  for (const [k, v] of cache.current) if (v) out.set(k, v);
  return out;
}

/** The Person filter's search: the server filters, a page at a time. */
const searchOperators = (search: string, cursor: string | null) =>
  api.get<{ items: PlatformUserRow[]; nextCursor: string | null }>(
    `/admin/platform-users?limit=20&search=${encodeURIComponent(search)}${cursor ? `&cursor=${cursor}` : ''}`,
  );

/** A real calendar day as YYYY-MM-DD: 2026-02-31 is refused, not rolled over. */
function isRealDate(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** Who did it: a name where we know one, the email a sign-in names, else the role. */
function actorOf(row: AuditRow, names: Map<string, string>): { who: string; role: string } {
  const m = meta(row);
  const role = roleLabel(row.actorRole ?? (typeof m.role === 'string' ? m.role : null));
  if (row.actorId && names.has(row.actorId)) return { who: names.get(row.actorId)!, role };
  // A sign-in happens before there is an actor; the row names the account.
  if (!row.actorId && row.action.startsWith('auth.') && typeof m.email === 'string') {
    return { who: names.get(m.email.toLowerCase()) ?? m.email, role };
  }
  if (row.actorId) return { who: `User #${row.actorId.slice(0, 8)}`, role };
  return { who: 'System', role: row.actorRole ? role : 'Automatic' };
}

/**
 * What a platform role without the read permission sees when it reaches one
 * of these screens by URL. Ops, support and finance are not given these pages
 * in their navigation, but a pasted link used to load the page, fire the
 * request, and put "Something went wrong — Missing required permission" with
 * a Try again that could never work. This says what the page is and that it
 * belongs to another role, and fires nothing.
 */
export function NotForYourRole({ title, what }: { title: string; what: string }) {
  return (
    <div className="space-y-4">
      <PageHeader title={title} />
      <EmptyState
        title="Not available to your role"
        hint={`${what} Ask a platform administrator if you need it.`}
      />
    </div>
  );
}

export function Audit({ embedded = false }: { embedded?: boolean } = {}) {
  if (!useAuth().permissions.has('audit.read')) {
    return <NotForYourRole title="Audit log" what="The audit log is read by platform administrators." />;
  }
  return <AuditTrail embedded={embedded} />;
}

/** `embedded`: inside Settings → Audit log, which carries the page heading. */
function AuditTrail({ embedded }: { embedded: boolean }) {
  // Every filter lives in the address, beside ?tab=audit, so a refresh or a
  // pasted link opens the same view. Other parameters are left alone.
  const [params, setParams] = useSearchParams();
  const filters: Record<string, string | undefined> = {
    area: params.get('area') ?? undefined,
    entityType: params.get('entityType') ?? undefined,
    role: params.get('role') ?? undefined,
    actor: params.get('actor') ?? undefined,
  };
  const q = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Math.max(1, Math.floor(Number(params.get('page')) || 1));
  const [term, setTerm] = useState(q);
  const [open, setOpen] = useState<AuditRow | null>(null);

  /** Writes params; any change but the page itself starts again at page 1. */
  const update = (next: Record<string, string | undefined>, keepPage = false) =>
    setParams((cur) => {
      const n = new URLSearchParams(cur);
      for (const [k, v] of Object.entries(next)) { if (v) n.set(k, v); else n.delete(k); }
      if (!keepPage) n.delete('page');
      return n;
    }, { replace: true });
  const setFilters = (next: Record<string, string | undefined> | ((f: Record<string, string | undefined>) => Record<string, string | undefined>)) =>
    update(typeof next === 'function' ? next(filters) : next);
  const setPage = (n: number) => update({ page: n > 1 ? String(n) : undefined }, true);

  // Typing is not a request per keystroke: the box updates at once, the
  // address (and so the request) 300 ms after the last key.
  useEffect(() => {
    const t = setTimeout(() => { if (term.trim() !== q) update({ q: term.trim() || undefined }); }, 300);
    return () => clearTimeout(t);
  }, [term]); // eslint-disable-line react-hooks/exhaustive-deps
  // Back/forward or a pasted link changes q under the box.
  useEffect(() => { setTerm((t) => (t.trim() === q ? t : q)); }, [q]);

  // A date typed or pasted into the address is checked before it is sent:
  // a real calendar day, and the start not after the end.
  const fromBad = from !== '' && !isRealDate(from);
  const toBad = to !== '' && !isRealDate(to);
  const orderBad = !fromBad && !toBad && from !== '' && to !== '' && from > to;
  const dateError = fromBad || toBad
    ? `${fromBad ? `“${from}”` : `“${to}”`} is not a real date. Use a day that exists, as YYYY-MM-DD.`
    : orderBad ? 'The start date is after the end date.' : null;
  const sendFrom = fromBad || orderBad ? '' : from;
  const sendTo = toBad || orderBad ? '' : to;

  const url = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (filters.area) p.set('action', filters.area);
    if (filters.entityType) p.set('entityType', filters.entityType);
    if (filters.role) p.set('actorRole', filters.role);
    if (filters.actor) p.set('actorId', filters.actor);
    if (sendFrom) p.set('from', sendFrom);
    if (sendTo) p.set('to', sendTo);
    if (q) {
      p.set('q', q);
      const worded = actionsWorded(q);
      if (worded.length) p.set('labelActions', worded.join(','));
    }
    return `/audit?${p.toString()}`;
  }, [page, filters.area, filters.entityType, filters.role, filters.actor, sendFrom, sendTo, q]);

  const trail = useQuery<NumberedPage<AuditRow>>(() => api.get(url), [url]);
  const total = trail.data?.total ?? 0;
  const rows = useMemo(() => trail.data?.items ?? [], [trail.data]);
  const names = useActorNames(rows);

  // The chosen person, as the picker shows it. From the address it arrives as
  // an id; the name is read back once.
  const [actorRow, setActorRow] = useState<PlatformUserRow | null>(null);
  useEffect(() => {
    if (!filters.actor) { setActorRow(null); return; }
    if (actorRow?.id === filters.actor) return;
    let live = true;
    api.get<PlatformUserRow>(`/admin/platform-users/${filters.actor}`)
      .then((u) => { if (live) setActorRow(u); })
      .catch(() => { if (live) setActorRow({ id: filters.actor!, fullName: `User #${filters.actor!.slice(0, 8)}`, email: '' } as PlatformUserRow); });
    return () => { live = false; };
  }, [filters.actor]); // eslint-disable-line react-hooks/exhaustive-deps
  const canSearchPeople = useAuth().permissions.has('user.read');

  const filtered = Boolean(filters.area || filters.entityType || filters.role || filters.actor || q || from || to);

  return (
    <div className="space-y-4">
      {embedded ? (
        <p className="text-small text-neutral-600">
          Every change the platform’s own staff made, every sign-in, and every read across companies — who, what and when. Nothing here can be edited or deleted, including by the platform.
        </p>
      ) : (
        <PageHeader
          title="Audit log"
          hint="Every change the platform's own staff made, every sign-in, and every read across companies — who, what and when. Nothing here can be edited or deleted, including by the platform."
        />
      )}

      <FilterBar
        filters={[
          { key: 'area', label: 'Activity', allLabel: 'All activity', options: AREAS },
          { key: 'entityType', label: 'Record', allLabel: 'Any record', options: RECORD_TYPES },
          { key: 'role', label: 'Role', allLabel: 'Any role', options: ROLE_OPTIONS },
        ]}
        value={{ area: filters.area, entityType: filters.entityType, role: filters.role }}
        onChange={(v) => update({ area: v.area, entityType: v.entityType, role: v.role })}
        more={(
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <label className="flex min-w-0 flex-1 items-center gap-1.5 text-caption text-neutral-600 sm:flex-none">
              <span className="shrink-0">From</span>
              <Input type="date" value={from} max={to || undefined} onChange={(e) => update({ from: e.target.value || undefined })}
                     invalid={fromBad || orderBad} aria-invalid={fromBad || orderBad || undefined} aria-describedby={dateError ? 'audit-date-error' : undefined}
                     className="h-10 min-w-0" aria-label="From date" />
            </label>
            <label className="flex min-w-0 flex-1 items-center gap-1.5 text-caption text-neutral-600 sm:flex-none">
              <span className="shrink-0">to</span>
              <Input type="date" value={to} min={from || undefined} onChange={(e) => update({ to: e.target.value || undefined })}
                     invalid={toBad || orderBad} aria-invalid={toBad || orderBad || undefined} aria-describedby={dateError ? 'audit-date-error' : undefined}
                     className="h-10 min-w-0" aria-label="To date" />
            </label>
            {(from || to) && (
              <button
                type="button"
                onClick={() => update({ from: undefined, to: undefined })}
                className="shrink-0 text-caption font-medium text-primary-600 hover:underline"
              >
                Clear dates
              </button>
            )}
          </div>
        )}
        moreActive={(from ? 1 : 0) + (to ? 1 : 0)}
      >
        <SearchInput
          value={term}
          onValueChange={setTerm}
          placeholder="Search action, person, email or record"
          aria-label="Search the audit trail"
          className="w-full sm:w-80"
        />
        {canSearchPeople && (
          <div className="w-full sm:w-64">
            <EntityCombobox<PlatformUserRow>
              value={actorRow}
              onChange={(u) => { setActorRow(u); update({ actor: u?.id }); }}
              fetchOptions={searchOperators}
              getKey={(u) => u.id}
              renderOption={(u) => (
                <span className="min-w-0">
                  <span className="block truncate text-small text-neutral-900">{u.fullName}</span>
                  {u.email && <span className="block truncate text-caption text-neutral-500">{u.email}</span>}
                </span>
              )}
              renderValue={(u) => u.fullName}
              placeholder="Anyone"
              searchPlaceholder="Search a platform user by name or email"
            />
          </div>
        )}
      </FilterBar>
      {dateError && (
        <p id="audit-date-error" role="alert" className="text-caption text-danger-600">
          {dateError} The list below ignores the date range until it is fixed.
        </p>
      )}

      <AuditTimeline
        total={total}
        rows={rows}
        loading={trail.loading && !trail.data}
        error={trail.error}
        onRetry={trail.reload}
        names={names}
        onOpen={setOpen}
        person={filters.actor}
        onPerson={(id) => setFilters((f) => ({ ...f, actor: f.actor === id ? undefined : id }))}
        filtered={filtered}
      />

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        disabled={trail.loading}
      />

      {open && <AuditDetail row={open} names={names} onClose={() => setOpen(null)} />}
    </div>
  );
}

/* ── The timeline ─────────────────────────────────────────────── */

/** What kind of act a row is, for its icon and colour. */
function kindOf(row: AuditRow): { icon: LucideIcon; chip: string } {
  const a = row.action.toLowerCase();
  // What the row says it did, in words: "read setup status", "list rate cards".
  const said = `${actionLabel(row.action)} ${recordName(row) ?? ''}`.toLowerCase();
  const reads = /^(read|view|list|download|export)\b/.test(said) || /\b(read|list|view)\b/.test(recordName(row)?.toLowerCase() ?? '');
  if (row.outcome !== 'SUCCESS' || a.includes('failed') || a.includes('denied')) return { icon: ShieldAlert, chip: 'bg-danger-100 text-danger-600' };
  if (a.startsWith('platform.impersonation')) return { icon: UserCog, chip: 'bg-accent-50 text-accent-600' };
  if (a.startsWith('auth.')) return { icon: LogIn, chip: 'bg-primary-50 text-primary-600' };
  if (reads) return { icon: Eye, chip: 'bg-info-100 text-info-600' };
  if (/(delete|remove|revoke|suspend|cancel|retire)/.test(a)) return { icon: Trash2, chip: 'bg-danger-100 text-danger-600' };
  if (/(create|add|invite|register|empanel|issue)/.test(a)) return { icon: Plus, chip: 'bg-success-100 text-success-600' };
  if (/(update|change|\bset\b|\.set|set-|edit|permission|approve|activate|assign|move)/.test(a)) return { icon: Pencil, chip: 'bg-warning-100 text-warning-600' };
  if (/(read|view|overview|dashboard|download|list|export|pipeline|trends|camps)/.test(a)) return { icon: Eye, chip: 'bg-info-100 text-info-600' };
  return { icon: ActivityIcon, chip: 'bg-neutral-100 text-neutral-600' };
}

interface Entry { row: AuditRow; times: number; views: string[] }

const isView = (row: AuditRow) => row.outcome === 'SUCCESS' && kindOf(row).icon === Eye;

/**
 * Consecutive repeats folded into one line: the same person doing the same
 * thing to the same record within ten minutes (a dashboard opened, refreshed
 * and opened again) is one entry "×3", not three rows that bury the change
 * made in between. The newest of them stands for the group.
 */
function fold(rows: AuditRow[]): Entry[] {
  const out: Entry[] = [];
  for (const row of rows) {
    const last = out[out.length - 1];
    const same = last && last.row.action === row.action && last.row.actorId === row.actorId
      && last.row.entityType === row.entityType && last.row.entityId === row.entityId && last.row.outcome === row.outcome
      && Math.abs(new Date(last.row.occurredAt).getTime() - new Date(row.occurredAt).getTime()) < 10 * 60_000;
    // A burst of views by one person (a dashboard opening reads four things
    // at once) is one entry listing what was looked at.
    const burst = !same && last && isView(row) && isView(last.row) && last.row.actorId === row.actorId
      && Math.abs(new Date(last.row.occurredAt).getTime() - new Date(row.occurredAt).getTime()) < 2 * 60_000;
    const label = actionLabel(row.action);
    if (same) last.times++;
    else if (burst) { last.times++; if (!last.views.includes(label)) last.views.push(label); }
    else out.push({ row, times: 1, views: [label] });
  }
  return out;
}

function dayOf(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });

/**
 * The trail as an activity timeline (owner, 29 Oct 2026): a coloured icon per
 * kind of act on a dotted line, "what happened by whom" in words, the time on
 * the right, and the record under it. Grouped by day. Above it, the people in
 * view with how many acts each — one click narrows to that person.
 */
function AuditTimeline({ total, rows, loading, error, onRetry, names, onOpen, person, onPerson, filtered }: {
  total: number; rows: AuditRow[]; loading: boolean; error: string | null; onRetry: () => void;
  names: Map<string, string>; onOpen: (row: AuditRow) => void;
  person?: string; onPerson: (id: string) => void; filtered: boolean;
}) {
  const entries = useMemo(() => fold(rows), [rows]);
  const who = useMemo(() => {
    const m = new Map<string, { id: string; name: string; count: number }>();
    for (const r of rows) {
      if (!r.actorId) continue;
      const name = actorOf(r, names).who;
      const cur = m.get(r.actorId) ?? { id: r.actorId, name, count: 0 };
      cur.count++; m.set(r.actorId, cur);
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [rows, names]);

  if (error) return <EmptyState title="The audit log could not be loaded" hint={error} action={<button type="button" className="text-small font-medium text-primary-700 hover:underline" onClick={onRetry}>Try again</button>} />;
  if (loading) {
    return (
      <div className="space-y-3 rounded-card border border-neutral-200 bg-white p-4" aria-busy="true">
        {Array.from({ length: 6 }, (_, i) => <div key={i} className="h-10 animate-pulse rounded bg-neutral-100" />)}
      </div>
    );
  }
  if (!entries.length) {
    return filtered
      ? <EmptyState title="Nothing matches" hint="Try other words, a wider date range, or clear a filter." />
      : <EmptyState title="Nothing recorded yet" hint="Sign-ins and every change made from this console appear here." />;
  }

  let lastDay = '';
  return (
    <div className="rounded-card border border-neutral-200 bg-white p-4 shadow-card sm:p-5">
      {(who.length > 1 || person) && (
        <div className="mb-4 flex flex-wrap items-center gap-2" aria-label="People in this view">
          <span className="text-caption font-medium text-neutral-600">People in this view:</span>
          {who.map((p) => (
            <button key={p.id} type="button" onClick={() => onPerson(p.id)} aria-pressed={person === p.id}
              className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption transition-colors',
                person === p.id ? 'border-primary-300 bg-primary-50 font-medium text-primary-700' : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50')}>
              <span aria-hidden className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-700">
                {p.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
              </span>
              {p.name}<span className="text-neutral-500">· {p.count}</span>
            </button>
          ))}
        </div>
      )}

      {entries.length < rows.length && (
        <p className="mb-3 text-caption text-neutral-500">
          {rows.length} entries on this page, shown as {entries.length} lines: repeats by the same person within minutes are folded together. Open a line for the full record.
        </p>
      )}
      <ol className="relative">
        {entries.map(({ row, times, views }, i) => {
          const day = dayOf(row.occurredAt);
          const header = day !== lastDay;
          lastDay = day;
          const { icon: Icon, chip } = kindOf(row);
          const { who: actor, role } = actorOf(row, names);
          // What changed something is what an audit is read for; reads and
          // sign-ins stay in the trail but step back.
          const quiet = row.outcome === 'SUCCESS' && (Icon === Eye || Icon === LogIn);
          const count = views.length > 1 ? `${times} reads` : times > 1 ? `${times} times` : null;
          const name = recordName(row);
          const lastOfDay = i === entries.length - 1 || dayOf(entries[i + 1]!.row.occurredAt) !== day;
          return (
            <Fragment key={row.id}>
              {header && (
                <li className={cn('pb-2 text-caption font-semibold uppercase tracking-wide text-neutral-500', i > 0 && 'pt-4')}>{day}</li>
              )}
              <li className="relative pl-10">
                {/* The dotted thread joining a day's entries. */}
                {!lastOfDay && <span aria-hidden className="absolute bottom-0 left-[13px] top-8 border-l-2 border-dotted border-neutral-200" />}
                <span aria-hidden className={cn('absolute left-0 top-1.5 flex h-7 w-7 items-center justify-center rounded-full', chip)}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <button type="button" onClick={() => onOpen(row)}
                  className="-mx-2 mb-1 block w-[calc(100%+1rem)] rounded-btn px-2 py-2 text-left hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                  <div className="flex items-start justify-between gap-3">
                    <p className={cn('min-w-0 text-small', quiet ? 'text-neutral-600' : 'text-neutral-700')}>
                      <span className={cn(quiet ? 'font-medium text-neutral-800' : 'font-semibold text-neutral-900')}>{views.length > 1 ? `Viewed ${views.length} pages` : actionLabel(row.action)}</span>
                      {' by '}<span className={cn('font-medium', quiet ? 'text-neutral-700' : 'text-neutral-900')}>{actor}</span>
                      {count && <span className="ml-1.5 whitespace-nowrap rounded-full bg-neutral-100 px-1.5 py-0.5 text-micro font-medium text-neutral-600">{count}</span>}
                      {row.outcome !== 'SUCCESS' && <span className="ml-1.5 align-middle"><Outcome outcome={row.outcome} /></span>}
                    </p>
                    <time dateTime={row.occurredAt} className="shrink-0 whitespace-nowrap text-caption tabular-nums text-neutral-500" title={formatDateTime(row.occurredAt)}>
                      {timeOf(row.occurredAt)}
                    </time>
                  </div>
                  <p className="mt-0.5 truncate text-caption text-neutral-500">
                    {views.length > 1 ? views.join(' · ') : <>{entityLabel(row.entityType)}{name ? ` · ${name}` : ''}</>}{role ? ` · ${role}` : ''}
                  </p>
                </button>
              </li>
            </Fragment>
          );
        })}
      </ol>
    </div>
  );
}

function AuditDetail({ row, names, onClose }: { row: AuditRow; names: Map<string, string>; onClose: () => void }) {
  const { who, role } = actorOf(row, names);
  const blocks = (['before', 'after', 'metadata'] as const).filter((k) => row[k] != null);
  const TITLE = { before: 'Before', after: 'After', metadata: 'Context' } as const;
  return (
    <Drawer title={actionLabel(row.action)} onClose={onClose} width="lg">
      <div className="space-y-5">
        <Facts
          columns={2}
          facts={[
            { label: 'When', value: formatDateTime(row.occurredAt) },
            { label: 'Outcome', value: row.outcome === 'SUCCESS' ? 'Done' : <Outcome outcome={row.outcome} /> },
            { label: 'Who', value: who, hint: role },
            { label: 'From', value: row.ip ?? 'Not recorded' },
            { label: 'Record', value: entityLabel(row.entityType), hint: recordName(row) ?? undefined },
            { label: 'Action key', value: <span className="font-mono text-small">{row.action}</span>, hint: 'What the trail stores. Quote this to an auditor.' },
            row.entityId ? { label: 'Record id', value: <span className="break-all font-mono text-small">{row.entityId}</span>, wide: true } : null,
          ]}
        />
        {blocks.length === 0 ? (
          <p className="text-small text-neutral-500">No before, after or context was recorded with this entry.</p>
        ) : blocks.map((key) => (
          <section key={key}>
            <h3 className="text-caption font-semibold uppercase tracking-wide text-neutral-600">{TITLE[key]}</h3>
            <pre className="mt-1.5 max-h-64 overflow-auto rounded-input bg-neutral-50 p-3 font-mono text-caption text-neutral-800">
              {JSON.stringify(row[key], null, 2)}
            </pre>
          </section>
        ))}
      </div>
    </Drawer>
  );
}

/**
 * A record's own history, for the bottom of a detail page. Same words as the
 * trail, so "Test changed" reads the same here and there.
 */
export function ActivityTable({ rows, loading, error, onRetry, emptyHint, showRecord }: {
  rows: AuditRow[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyHint?: string;
  /** For an actor's history, where each row is about a different record. */
  showRecord?: boolean;
}) {
  const names = useActorNames(rows);
  const [open, setOpen] = useState<AuditRow | null>(null);
  return (
    <>
      <DataTable<AuditRow>
        rows={rows} loading={loading} error={error} onRetry={onRetry}
        rowKey={(r) => r.id}
        onRowClick={setOpen}
        empty={{ title: 'Nothing recorded yet', hint: emptyHint }}
        columns={[
          {
            key: 'what', header: 'What happened', alwaysVisible: true,
            cell: (r) => (
              <div className="min-w-0">
                <div className="text-small text-neutral-900">{actionLabel(r.action)}</div>
                {showRecord && (
                  <div className="truncate text-caption text-neutral-500">
                    {entityLabel(r.entityType)}{recordName(r) ? ` · ${recordName(r)}` : ''}
                  </div>
                )}
              </div>
            ),
          },
          ...(!showRecord ? [{
            key: 'who', header: 'Who',
            cell: (r: AuditRow) => <span className="text-caption text-neutral-700">{actorOf(r, names).who}</span>,
          }] : []),
          {
            key: 'when', header: 'When', align: 'right',
            cell: (r) => <span className="whitespace-nowrap text-caption tabular-nums text-neutral-700">{formatDateTime(r.occurredAt)}</span>,
          },
          { key: 'outcome', header: 'Outcome', align: 'right', cell: (r) => <Outcome outcome={r.outcome} /> },
        ]}
      />
      {open && <AuditDetail row={open} names={names} onClose={() => setOpen(null)} />}
    </>
  );
}
